
import React, { useState } from 'react';
import { useServerTags } from '@/hooks/useServerTags';
import { ProposeTagDialog } from '@/components/ProposeTagDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Filter } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TagsFilterProps {
  selectedTags: string[];
  onTagToggle: (tag: string) => void;
  onClearTags: () => void;
}

export const TagsFilter: React.FC<TagsFilterProps> = ({ selectedTags, onTagToggle, onClearTags }) => {
  const { t } = useLanguage();
  const { data: availableTags, isLoading } = useServerTags();
  const [isOpen, setIsOpen] = useState(false);

  const handleTagToggle = (tagName: string) => {
    onTagToggle(tagName);
  };

  const clearAllTags = () => {
    onClearTags();
  };

  if (isLoading) {
    return (
      <div className="modern-card p-6">
        <div className="h-8 bg-slate-700 rounded animate-pulse mb-4"></div>
        <div className="grid grid-cols-2 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 bg-slate-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700/50 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Filter className="w-4 h-4 text-gaming-primary" />
          {t('tags.filterByTags')}
        </h3>
        <div className="flex items-center gap-2">
          <ProposeTagDialog />
          {selectedTags.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllTags}
              className="text-red-400 hover:text-red-300 h-8 px-2"
            >
              <X className="w-3 h-3 mr-1" />
              {t('tags.clearAll')}
            </Button>
          )}
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="mb-2">
          <div className="flex flex-wrap gap-1">
            {selectedTags.map((tagName) => {
              const tagObj = availableTags?.find((t) => t.name === tagName);
              return (
                <Badge 
                  key={tagName} 
                  className="bg-gaming-primary/20 text-gaming-primary border-gaming-primary/30 cursor-pointer hover:bg-gaming-primary/30 text-xs px-2 py-1 h-6"
                  onClick={() => handleTagToggle(tagName)}
                >
                  {tagObj?.displayName || tagName}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {availableTags?.map((tag) => (
          <Button
            key={tag.id}
            variant={selectedTags.includes(tag.name) ? "default" : "ghost"}
            size="sm"
            className={`text-[10px] justify-start h-6 px-1.5 py-0 ${
              selectedTags.includes(tag.name)
                ? 'bg-gaming-primary text-white'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
            onClick={() => handleTagToggle(tag.name)}
            aria-label={`Filtrer par ${tag.displayName || tag.name}`}
          >
            <div 
              className="w-2 h-2 rounded-full mr-1.5 flex-shrink-0" 
              style={{ backgroundColor: tag.color }}
              aria-hidden="true"
            ></div>
            <span className="truncate">{tag.displayName || tag.name}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};
